import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import {
  GripVertical, Pencil, X, Check, Eye, EyeOff, Save, RotateCcw,
  RefreshCcw, ShieldCheck, ChevronDown,
} from "lucide-react";

/**
 * "Admin menu" arrangement panel — sits inside AdminMenuDirectory under the
 * "Admin menu" tab. Mirrors the member-sidebar drag/rename/toggle UX but
 * targets the /admin panel tiles (backed by /api/admin/admin-menu).
 *
 * Only super admins can reach this — enforced server-side by
 * require_super_admin on GET /admin/admin-menu.
 */
export default function AdminMenuArrange() {
  const [tiles, setTiles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [initial, setInitial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const drag = useRef(null);
  const [hint, setHint] = useState(null); // {id, position}
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/admin-menu");
      const sorted = (data.tiles || []).slice().sort((a, b) => a.order - b.order);
      setTiles(sorted);
      setRoles(data.roles || []);
      setInitial(sorted);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const dirty = useMemo(() => (
    JSON.stringify(_serialize(tiles)) !== JSON.stringify(_serialize(initial))
  ), [tiles, initial]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/admin-menu", {
        tiles: tiles.map((t, idx) => ({
          id: t.id,
          label: t.label,
          order: idx,
          allowed_roles: t.allowed_roles || [],
        })),
        disabled: tiles.filter((t) => !t.enabled).map((t) => t.id),
      });
      toast.success("Admin menu saved — reload /admin to see the new order.");
      setInitial(tiles);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const revert = () => { setTiles(initial); setRenamingId(null); };

  const reset = async () => {
    if (!window.confirm("Reset the admin menu to the default arrangement?")) return;
    try {
      await api.post("/admin/admin-menu/reset");
      toast.success("Admin menu reset to defaults.");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  // ---- Toggle enabled ----
  const toggleEnabled = (id) => {
    setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t)));
  };

  // ---- Rename ----
  const startRename = (id, current) => {
    setRenamingId(id);
    setRenameValue(current);
  };
  const commitRename = () => {
    const val = renameValue.trim();
    if (!val) { setRenamingId(null); return; }
    setTiles((prev) => prev.map((t) => (t.id === renamingId ? { ...t, label: val } : t)));
    setRenamingId(null);
  };
  const cancelRename = () => { setRenamingId(null); setRenameValue(""); };

  // ---- Roles ----
  const toggleRole = (tileId, roleKey) => {
    setTiles((prev) => prev.map((t) => {
      if (t.id !== tileId) return t;
      const cur = t.allowed_roles || [];
      const next = cur.includes(roleKey) ? cur.filter((r) => r !== roleKey) : [...cur, roleKey];
      return { ...t, allowed_roles: next };
    }));
  };
  const clearRoles = (tileId) => {
    setTiles((prev) => prev.map((t) => (t.id === tileId ? { ...t, allowed_roles: [] } : t)));
  };

  // ---- Drag & drop ----
  const onDragStart = (e, id) => {
    drag.current = id;
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e, id) => {
    if (!drag.current || drag.current === id) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const before = (e.clientY - rect.top) < rect.height / 2;
    setHint({ id, position: before ? "before" : "after" });
  };
  const onDrop = (e, id) => {
    if (!drag.current) return;
    e.preventDefault();
    const src = drag.current;
    if (src !== id) {
      const rect = e.currentTarget.getBoundingClientRect();
      const before = (e.clientY - rect.top) < rect.height / 2;
      moveNear(src, id, before);
    }
    drag.current = null;
    setHint(null);
  };
  const onDragEnd = () => { drag.current = null; setHint(null); };

  const moveNear = (sourceId, targetId, before) => {
    setTiles((prev) => {
      const src = prev.find((t) => t.id === sourceId);
      if (!src) return prev;
      const filtered = prev.filter((t) => t.id !== sourceId);
      const tIdx = filtered.findIndex((t) => t.id === targetId);
      if (tIdx < 0) return prev;
      filtered.splice(before ? tIdx : tIdx + 1, 0, src);
      return filtered.map((t, idx) => ({ ...t, order: idx }));
    });
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading admin menu…</p>;
  }

  return (
    <div data-testid="admin-menu-arrange">
      <div className="flex flex-wrap items-center gap-2 justify-between mb-4">
        <p className="text-sm text-muted-foreground max-w-xl">
          Reorder, rename, hide, or role-gate the rectangles shown on the
          <span className="font-semibold text-foreground"> /admin</span> page.
          Changes apply after the next page load.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={reset}
            data-testid="admin-menu-reset"
            className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted"
          >
            <RefreshCcw className="w-3.5 h-3.5" /> Reset
          </button>
          {dirty ? (
            <>
              <button onClick={revert} disabled={saving} data-testid="admin-menu-revert"
                className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">
                <RotateCcw className="w-3.5 h-3.5" /> Revert
              </button>
              <button onClick={save} disabled={saving} data-testid="admin-menu-save"
                className="btn-amber inline-flex items-center gap-1 text-xs disabled:opacity-60">
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              <Eye className="w-3 h-3" /> Live
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-1" data-testid="admin-menu-list">
        {tiles.map((tile) => {
          const Icon = Icons[tile.icon] || Icons.Sparkles;
          const showBefore = hint?.id === tile.id && hint.position === "before";
          const showAfter = hint?.id === tile.id && hint.position === "after";
          const renaming = renamingId === tile.id;
          return (
            <React.Fragment key={tile.id}>
              {showBefore && <_Divider />}
              <div
                draggable
                onDragStart={(e) => onDragStart(e, tile.id)}
                onDragOver={(e) => onDragOver(e, tile.id)}
                onDrop={(e) => onDrop(e, tile.id)}
                onDragEnd={onDragEnd}
                data-testid={`admin-menu-tile-${tile.id}`}
                className={[
                  "group flex items-center gap-3 py-2 pl-2 pr-3 rounded-lg border",
                  "cursor-move select-none transition-colors",
                  tile.enabled ? "border-shell-border bg-white hover:bg-muted/50"
                               : "border-shell-border/50 bg-muted/20 opacity-70",
                ].join(" ")}
              >
                <GripVertical className="w-4 h-4 text-muted-foreground/60 shrink-0" />
                <span className="shrink-0 grid place-items-center w-8 h-8 rounded-lg bg-muted text-foreground/70">
                  <Icon className="w-4 h-4" />
                </span>
                {renaming ? (
                  <_Rename
                    value={renameValue}
                    onChange={setRenameValue}
                    onCommit={commitRename}
                    onCancel={cancelRename}
                    testid={`admin-menu-rename-input-${tile.id}`}
                  />
                ) : (
                  <button
                    onClick={() => startRename(tile.id, tile.label)}
                    className="text-sm font-semibold flex-1 text-left truncate hover:text-primary transition"
                    data-testid={`admin-menu-label-${tile.id}`}
                  >
                    {tile.label}
                  </button>
                )}
                <button
                  onClick={() => startRename(tile.id, tile.label)}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-muted text-muted-foreground transition-opacity"
                  title="Rename"
                  data-testid={`admin-menu-rename-${tile.id}`}
                >
                  <Pencil className="w-3 h-3" />
                </button>
                <_RoleSelector
                  tileId={tile.id}
                  selected={tile.allowed_roles || []}
                  catalog={roles}
                  onToggle={(rk) => toggleRole(tile.id, rk)}
                  onClear={() => clearRoles(tile.id)}
                />
                <_Toggle
                  id={tile.id}
                  on={!!tile.enabled}
                  onToggle={() => toggleEnabled(tile.id)}
                />
              </div>
              {showAfter && <_Divider />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local UI helpers (kept local to avoid touching AdminMenuDirectory's copies)
// ---------------------------------------------------------------------------

function _serialize(tiles) {
  return tiles.map((t) => ({
    id: t.id, label: t.label, enabled: !!t.enabled,
    allowed_roles: [...(t.allowed_roles || [])].sort(),
  }));
}

function _Divider() {
  return <div className="h-1 rounded-full bg-primary/70 my-0.5"
              data-testid="admin-menu-drop-divider" />;
}

function _Rename({ value, onChange, onCommit, onCancel, testid }) {
  return (
    <div className="flex items-center gap-1 flex-1 min-w-0">
      <input
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit();
          else if (e.key === "Escape") onCancel();
        }}
        onBlur={onCommit}
        data-testid={testid}
        className="flex-1 min-w-0 rounded border border-primary/50 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      <button onMouseDown={(e) => { e.preventDefault(); onCommit(); }}
              className="p-1 rounded text-emerald-600 hover:bg-emerald-50" title="Save (Enter)">
        <Check className="w-3.5 h-3.5" />
      </button>
      <button onMouseDown={(e) => { e.preventDefault(); onCancel(); }}
              className="p-1 rounded text-rose-500 hover:bg-rose-50" title="Cancel (Esc)">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function _Toggle({ id, on, onToggle }) {
  return (
    <button
      onClick={onToggle}
      role="switch"
      aria-checked={on}
      data-testid={`admin-menu-toggle-${id}`}
      title={on ? "Click to hide from /admin" : "Click to show on /admin"}
      className={[
        "relative shrink-0 h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30",
        on ? "bg-primary" : "bg-muted",
      ].join(" ")}
    >
      <span className={[
        "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
        on ? "translate-x-5" : "translate-x-0.5",
      ].join(" ")}>
        {on ? <Eye className="w-3 h-3 m-0.5 text-primary" />
            : <EyeOff className="w-3 h-3 m-0.5 text-muted-foreground" />}
      </span>
    </button>
  );
}

function _RoleSelector({ tileId, selected, catalog, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const all = selected.length === 0;
  const summary = all ? "All admins" : selected.map((k) =>
    catalog.find((r) => r.key === k)?.label || k
  ).join(", ");
  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`admin-menu-roles-${tileId}`}
        title={`Visible to: ${summary}`}
        className={[
          "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition",
          all ? "border-shell-border text-muted-foreground hover:bg-muted"
              : "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20",
        ].join(" ")}
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        <span className="max-w-[8rem] truncate">{summary}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute z-40 right-0 top-full mt-1 min-w-[11rem] rounded-lg border border-shell-border bg-white shadow-lg p-2">
          <p className="px-1.5 py-1 text-[9.5px] font-bold uppercase tracking-widest text-muted-foreground">
            Visible to
          </p>
          {catalog.map((r) => {
            const on = selected.includes(r.key);
            return (
              <label
                key={r.key}
                className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-muted cursor-pointer text-sm"
                data-testid={`admin-menu-role-option-${tileId}-${r.key}`}
              >
                <input type="checkbox" checked={on} onChange={() => onToggle(r.key)}
                       className="accent-primary" />
                <span className="flex-1">{r.label}</span>
              </label>
            );
          })}
          <div className="border-t border-shell-border mt-1 pt-1">
            <button
              onClick={onClear}
              className="w-full text-left px-1.5 py-1 text-xs text-muted-foreground hover:bg-muted rounded"
              data-testid={`admin-menu-roles-clear-${tileId}`}
            >
              All admins (no restriction)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
